import { Link } from 'react-router-dom';
import {  Info, Zap } from 'lucide-react';
import type { DocumentPriority } from '../lib/types';
import SourceLogo from './SourceLogo';

export interface LinkedFileRef {
  file_id: string;
  source_filename: string;
  source: string;
  source_type: string;
  source_path?: string | null;
  status: 'active' | 'deleted';
  /** A document Navaris generated for this type. */
  generated?: boolean;
}

export type DocStatus = 'published' | 'candidates' | 'missing' | 'pending';

export interface DocTypeRow {
  id: string;
  name: string;
  description?: string;
  notes?: string | null;
  priority: DocumentPriority;
  status: DocStatus;
  pending: boolean;
  linked_files: LinkedFileRef[];
}

export function DocTypeNotes({ notes }: { notes: string }) {
  return (
    <div className="flex items-start gap-2 px-3.5 py-2.5 bg-sky-50 border-t border-sky-200 text-[12.5px] text-sky-900 leading-relaxed">
      <Info className="w-3.5 h-3.5 text-sky-600 shrink-0 mt-0.5" />
      <span className="whitespace-pre-wrap min-w-0">{notes}</span>
    </div>
  );
}

export type PillStyle = { label: string; cls: string };

export const priorityStyles: Record<DocumentPriority, PillStyle> = {
  required: { label: 'Required', cls: 'bg-red-50 text-red-700 border border-red-200' },
  recommended: { label: 'Recommended', cls: 'bg-amber-50 text-amber-700 border border-amber-200' },
  optional: { label: 'Optional', cls: 'bg-coolslate/10 text-coolslate border border-coolslate/20' },
};

export function Pill({ style, shrink = false }: { style: PillStyle; shrink?: boolean }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium ${shrink ? 'shrink-0 ' : ''}${style.cls}`}
    >
      {style.label}
    </span>
  );
}

// Required first, optional last, matching priorityStyles' insertion order.
export const priorityRank: Record<DocumentPriority, number> = {
  required: 0,
  recommended: 1,
  optional: 2,
};

export const statusStyles: Record<DocStatus, PillStyle> = {
  published: { label: 'Published', cls: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
  candidates: { label: 'Candidates', cls: 'bg-amber-50 text-amber-700 border border-amber-200' },
  missing: { label: 'Missing', cls: 'bg-red-50 text-red-700 border border-red-200' },
  pending: { label: 'Pending', cls: 'bg-coolslate/10 text-coolslate border border-coolslate/20' },
};

/**
 * Renders a doc type as a single card. When published (i.e. one or more
 * linked or generated files exist), the card has a header (icon + name +
 * description + Published badge) and a table of the linked files below.
 * When not published, it's a one-row card with priority + status badges.
 * Used on the workspace index.
 */
export function DocTypeBlock({ doc }: { doc: DocTypeRow }) {
  if (doc.linked_files.length > 0) {
    return (
      <div className="border border-sage/30 bg-sage/5 rounded-lg overflow-hidden">
        <div className="flex items-center gap-3 px-3.5 py-3">
          <div className="flex-1 min-w-0">
            <Link
              to={`/types/${doc.id}`}
              className="text-sm font-medium text-graphite hover:text-brand-dark transition-colors truncate block"
            >
              {doc.name}
            </Link>
            {doc.description && (
              <span className="text-[12px] text-coolslate truncate block mt-0.5">
                {doc.description}
              </span>
            )}
          </div>
          <Pill style={priorityStyles[doc.priority]} shrink />
          <Pill style={statusStyles.published} shrink />
        </div>
        {doc.notes && <DocTypeNotes notes={doc.notes} />}
        <div className="border-t border-sage/20 bg-white/40">
          {doc.linked_files.map(file => {
            const isDeleted = file.status === 'deleted';
            const isGenerated = !!file.generated;
            const hasPath = isGenerated || (file.source_path && file.source_path.length > 0);
            const pathLabel = isGenerated ? 'Generated' : (file.source_path ?? '');
            return (
              <Link
                key={file.file_id}
                to={`/files/${file.file_id}`}
                className={`flex items-center gap-2 px-3.5 py-2 text-sm border-t border-sage/20 first:border-t-0 hover:bg-sage/10 transition-colors group ${isDeleted ? 'opacity-50' : ''}`}
              >
                <span className="inline-flex items-center gap-1.5 text-[12px] text-coolslate shrink-0">
                  <SourceLogo type={file.source_type} size="sm" />
                  <span>{file.source}</span>
                </span>
                {hasPath && (
                  <>
                    <span className="text-[12px] text-coolslate/60 shrink-0">/</span>
                    {isGenerated ? (
                      <span className="inline-flex items-center gap-1 text-[12px] text-coolslate shrink-0">
                        <Zap className="w-3.5 h-3.5 text-emerald-600" />
                        Generated
                      </span>
                    ) : (
                      <span
                        className="text-[12px] text-coolslate truncate min-w-0"
                        title={pathLabel}
                      >
                        {pathLabel}
                      </span>
                    )}
                  </>
                )}
                <span className="text-[12px] text-coolslate/60 shrink-0">/</span>
                <span className={`text-[12px] text-graphite group-hover:text-brand-dark transition-colors truncate min-w-0 ${isDeleted ? 'line-through' : ''}`}>
                  {file.source_filename}
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="border border-sage/30 bg-sage/5 rounded-lg overflow-hidden hover:bg-sage/10 transition-colors group">
      <Link
        to={`/types/${doc.id}`}
        className="flex items-center gap-3 px-3.5 py-3"
      >
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-graphite group-hover:text-brand-dark transition-colors truncate block">
            {doc.name}
          </span>
          {doc.description && (
            <span className="text-[12px] text-coolslate truncate block mt-0.5">
              {doc.description}
            </span>
          )}
        </div>
        <Pill style={priorityStyles[doc.priority]} shrink />
        <Pill style={statusStyles[doc.status]} shrink />
      </Link>
      {doc.notes && <DocTypeNotes notes={doc.notes} />}
    </div>
  );
}
